import { z } from 'zod';

export type InvoicePaymentStatus =
  | 'created'
  | 'cancelled'
  | 'completed'
  | 'payment-failed'
  | 'payment-pending'
  | 'refunded'
  | 'refund-failed';

export enum InvoiceStatus {
  DRAFT = 'draft',
  SENT = 'sent',
  PAID = 'paid',
  PAYMENT_FAILED = 'payment-failed',
  OVERDUE = 'overdue',
  CANCELLED = 'cancelled',
  DELETED = 'deleted',
  PARTIALLY_PAID = 'partially-paid',
}

export const invoiceStatusEnumZ = z.enum([
  'draft',
  'sent',
  'paid',
  'payment-failed',
  'overdue',
  'cancelled',
  'deleted',
  'partially-paid',
]);

export const invoiceItemSchema = z.object({
  productId: z.string().optional(),
  label: z.string().min(1),
  description: z.string().optional(),
  quantity: z.number().positive(),
  collectTaxes: z.boolean(),
  totalTaxAmountInCents: z.number().nonnegative(),
  unitPriceInCents: z.number().nonnegative(),
  totalPriceInCents: z.number().nonnegative(),
});

export const createInvoiceSchema = z.object({
  customerId: z.string(),
  dueDate: z
    .union([z.date(), z.string()])
    .transform((val) => new Date(val))
    .optional(),
  items: z.array(invoiceItemSchema).min(1),
  customerNotes: z.string().optional(),
  billingAddressId: z.string().nullable().optional(),
});

export const calcInvoiceTaxesSchema = z.object({
  customerId: z.string(),
  items: z.array(invoiceItemSchema).min(1),
});

export const updateInvoiceSchema = z.object({
  invoiceId: z.string(),
  status: invoiceStatusEnumZ.optional(),
  dueDate: z
    .union([z.date(), z.string()])
    .transform((val) => new Date(val))
    .optional(),
  items: z.array(invoiceItemSchema).optional(),
  customerNotes: z.string().optional(),
  billingAddressId: z.string().nullable().optional(),
  userId: z.string().optional(),
});

export const invoiceFiltersSchema = z.object({
  status: invoiceStatusEnumZ.optional(),
  customerId: z.string().optional(),
  dateFrom: z.date().optional(),
  dateTo: z.date().optional(),
  search: z.string().optional(),
  sortBy: z.enum(['createdAt', 'dueDate', 'totalInCents', 'status']).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  offset: z.number().optional(),
  limit: z.number().optional(),
});

export type InvoiceItem = z.infer<typeof invoiceItemSchema>;
